// check with team on approach we want here
import Service, { service } from '@ember/service';
import type { Store as StoreService } from '@warp-drive/core';
import type { UploadFile } from 'ember-file-upload';
import type { FileHistory as FileHistoryModel } from 'tio-data/data/resources/file-history.type';
import type DigitalAssetsService from './digital-assets';
import type FeaturesService from './features';
import type { Company } from 'tio-data/data/resources/company.type';
import { tracked } from '@glimmer/tracking';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { buildTioAuthHeaders } from 'tio-data/utils/auth-headers';
import { saveRecord } from 'tio-data/requests/save-record';

type SerializableValue =
  string | number | boolean | null | SerializableValue[] | SerializableRecord;
type SerializableRecord = { [key: string]: SerializableValue };
type FileHistoryPayload = {
  data: {
    type: 'file-history';
    id: string;
    attributes: SerializableRecord;
  };
};

export default class DataTransferAgentService extends Service {
  @service declare store: StoreService;
  @service declare digitalAssets: DigitalAssetsService;
  @service declare features: FeaturesService;

  @tracked reloadKey = 0;
  //not part of tuition-assistance service to keep it clean and use outside of TA if needed.

  createDataTransferAgentFileHistory = async (
    uploadFile: UploadFile,
    options: { company: Company; operationId: string }
  ): Promise<FileHistoryModel> => {
    const { company, operationId } = options;

    // get API path for raw file assets
    // WarpDrive migration (Phase 2.5): replaces adapter.urlForCreateRecord.
    // buildBaseURL with op:'query' produces the same collection URL as
    // urlForCreateRecord — the POST vs GET distinction is in the fetch options, not the path.
    const urlOptions: QueryUrlOptions = {
      identifier: { type: 'raw-file-digital-assets' },
      op: 'query',
      resourcePath: 'raw-file-digital-assets',
    };
    const collectionPath = buildBaseURL(urlOptions);

    // set headers
    // WarpDrive migration (Phase 2.5): replaces adapter.headers mutation.
    const headers = buildTioAuthHeaders({
      apiKey: this.features.ENV!.apiKey,
      contentType: 'application/vnd.api+json',
    });

    // create asset record through API
    const assetResponse = await fetch(`${collectionPath}/s3-signed-url`, {
      headers: headers,
      method: 'POST',
      body: JSON.stringify({ filename: uploadFile.file.name, public_image: false }),
    });

    // read asset response content and upload binary to signed url
    const assetDetails = await assetResponse.json();
    await uploadFile.uploadBinary(assetDetails.url, { method: 'PUT' });

    // create and save raw file asset
    const rawFileDigitalAsset = this.digitalAssets.createAssetFromS3Details(
      'raw-file-digital-asset',
      assetDetails
    );

    // Set content type from the uploaded file (must be text/csv for partner migration files)
    rawFileDigitalAsset.contentType = uploadFile.file.type || 'text/csv';

    // WarpDrive mutation builder (Phase 2.7): replaces record.save().
    await saveRecord(this.store, rawFileDigitalAsset); // verify the saving part. what exactly gets saved for digitalassets

    const contentType = rawFileDigitalAsset.contentType;
    // create and save migration file history
    const recordType = 'PartnerDataTransfer';

    const dataTransferAgentFileHistory = this.store.createRecord('file-history', {
      rawFileDigitalAsset: rawFileDigitalAsset,
      company: company,
      operationId: operationId,
      contentType: contentType,
      recordType: recordType,
    }) as FileHistoryModel;

    // WarpDrive mutation builder (Phase 2.7): replaces record.save().
    await saveRecord(this.store, dataTransferAgentFileHistory);
    return dataTransferAgentFileHistory;
  };

  //re-visit this method for preprocessed
  updateDataTransferAgentFileHistory = (attributes: Record<string, unknown>): FileHistoryModel => {
    const { id, ...attributeValues } = attributes;

    if (typeof id !== 'string') {
      throw new Error('File history update requires a string id.');
    }

    const payload: FileHistoryPayload = {
      data: {
        type: 'file-history',
        id,
        attributes: attributeValues as SerializableRecord,
      },
    };

    const fileHistory = this.store.push<FileHistoryModel>(payload);

    if (!fileHistory) {
      throw new Error('Failed to update file history.');
    }

    return fileHistory;
  };

  triggerReload() {
    this.reloadKey++;
  }
}
